import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { mergeExistingFields } from "helpers/mergeExistingFields";
import instance from "api/apiClient";
import { apiRoutes } from "configs/apiRoutes";
import { buildSchedulerWriteBody } from "./serializeSchedulerItem";

export type IScheduler =
| { 
  type: 'interval';
  interval: number; // minutes
  days: number[];
  }
| {
  type: 'specific';
  specific: string[];
  days: number[];
  }

export const schedulerTypes: Array<IScheduler['type']> = ['interval', 'specific']; 

export interface SchedulerItem {
  id: number;
  title: string;
  service: string;
  type: string;
  isActive: number;
  schedule: IScheduler;
}

export type OrderFields = Extract<'title' | 'service' | 'type' | 'isActive', keyof SchedulerItem>;

export interface GetSchedulerListPayload {
  limit: number;
  offset: number;
  title?: string;
  order?: {
    [k in OrderFields]?: 'asc' | 'desc' | 'none';
  },
}

export interface SchedulerState {
  list: SchedulerItem[];
  isListFetched: boolean;
  isItemFetched: boolean;
  activePage: number;
  totalItems: number;
  totalPages: number;
  selectedItems: number[];
  draftItem: Partial<SchedulerItem>;
  processingItemId?: SchedulerItem['id'];
  isItemFetching: boolean;
}

export interface SchedulerResponse {
  items: SchedulerItem[];
  totalItems: number;
  totalPages: number;
}


const getSchedulerListPayload = createAsyncThunk(
  'scheduler/list',
  async (data: GetSchedulerListPayload) => {
    try {
      const result = await instance.post(`${apiRoutes.schedulerList}`, { ...data });

      return result.data as SchedulerResponse;
      
    } catch (error) {
      throw error;
    }
  }
);

const deleteSchedulerItem = createAsyncThunk(
  'scheduler/delete',
  async (id: SchedulerItem['id']) => {
    try {
      const result = await instance.delete(`${apiRoutes.schedulerItem}/delete/${id}`);

      return true;
    } catch (error) {
      throw error;
    }
  }
);

const getSchedulerItem = createAsyncThunk(
  'scheduler/getItem',
  async (id: SchedulerItem['id']) => {
    try {
      // const result = await instance.get(`${apiRoutes.schedulerItem}/${id}`);
      const result = await instance.post(`${apiRoutes.schedulerList}`);
      const rawData = result.data as SchedulerResponse;
      const data = rawData.items.find(s => s.id === id);

      return data as SchedulerItem;
    } catch (error) {
      throw error;
    }
  }
);

const createSchedulerItem = createAsyncThunk(
  'scheduler/createtItem',
  async (data: Partial<SchedulerItem>) => {
    try {
      // const result = await instance.get(`${apiRoutes.schedulerItem}/${id}`);
      const result = await instance.post(
        `${apiRoutes.schedulerItemCreate}`,
        buildSchedulerWriteBody(data),
      );

      return true;
    } catch (error) {
      throw error;
    }
  }
);

const updateSchedulerItem = createAsyncThunk(
  'scheduler/updateItem',
  async (data: Partial<SchedulerItem>) => {
    try {
      const result = await instance.put(
        `${apiRoutes.schedulerItemUpdate}/${data.id}`,
        buildSchedulerWriteBody(data),
      );

      return true;
    } catch (error) {
      throw error;
    }
  }
);

const initialDraftItem: Partial<SchedulerItem> = {
  title: '',
  type: '',
  service: '',
  schedule: {
    type: 'interval',
    interval: 1,
    days: []
  }
};

const initialState: SchedulerState = {
  list: [],
  isListFetched: false,
  isItemFetched: false,
  activePage: 1,
  totalItems: 0,
  totalPages: 0,
  selectedItems: [],
  processingItemId: undefined,
  draftItem: initialDraftItem,
  isItemFetching: false
}

const schedulerSlice = createSlice({
  name: 'scheduler',
  initialState,
  reducers: {
    handlePaginate: (state, action: PayloadAction<number>) => {
      state.activePage = action.payload;
      state.isListFetched = false;
      state.selectedItems = [];
    },
    handleSelectAll: (state) => {
      state.selectedItems = state.selectedItems.length !== state.list.length
        ? state.list.map((item) => item.id)
        : [];
    },
    handleSelect: (state, action: PayloadAction<number>) => {
      if (state.selectedItems.includes(action.payload)) {
        state.selectedItems = state.selectedItems.filter(
          (el) => el !== action.payload,
        )
      } else {
        state.selectedItems.push(action.payload);
      }
    },
    handleClearFields: (state) => {
      state.draftItem = initialDraftItem;
      state.isItemFetched = false;
    },
    handleUpdateFieldDraftItem: (state, payload: PayloadAction<Partial<SchedulerItem>>) => {
      state.draftItem = {
        ...(state.draftItem ?? ({} as SchedulerItem)),
        ...payload.payload,
      };
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getSchedulerListPayload.fulfilled, (state, action) => {
      state.isListFetched = true;
      state.list = action.payload.items ?? [];
      // map data
      const rawData = action.payload.items ?? [];
      const data = [];

      if (rawData.length) {
        const mappedData = rawData.map(itm => {
          const { id, title, service, type, isActive, schedule } = itm;

          return {
            id, title, service, type, schedule, isActive
          }
        });
        
        data.push(...mappedData);
      }

      state.list = data;
      state.totalItems = action.payload?.totalItems ?? 1;
      state.totalPages = action.payload?.totalPages ?? 1;
    });
    builder.addCase(deleteSchedulerItem.pending, (state, action) => {
      state.processingItemId = action.meta.arg || undefined;
    });
    builder.addCase(deleteSchedulerItem.fulfilled, (state) => {
      state.processingItemId = undefined;
      state.isListFetched = false;
    });
    builder.addCase(deleteSchedulerItem.rejected, (state) => {
      state.processingItemId = undefined;
    });
    builder.addCase(getSchedulerItem.fulfilled, (state, action) => {
      state.isItemFetched = true;
      state.draftItem = action.payload || initialDraftItem;
    });
    builder.addCase(getSchedulerItem.pending, (state) => {
      state.isItemFetched = false;
    });
    builder.addCase(createSchedulerItem.pending, (state) => {
      state.isItemFetching = true;
    });
    builder.addCase(createSchedulerItem.fulfilled, (state, action) => {
      state.isItemFetching = false;
      state.isListFetched = false;
    });
    builder.addCase(createSchedulerItem.rejected, (state) => {
      state.isItemFetching = false;
    });
    builder.addCase(updateSchedulerItem.pending, (state, action) => {
      state.isItemFetching = true;
      state.processingItemId = action.meta.arg.id;
    });
    builder.addCase(updateSchedulerItem.fulfilled, (state, action) => {
      state.isItemFetching = false;
      state.list = state.list.map((item) =>
        item.id === action.meta.arg.id
          ? mergeExistingFields(item, action.meta.arg)
          : item,
      );
      state.processingItemId = undefined;
    });
    builder.addCase(updateSchedulerItem.rejected, (state) => {
      state.isItemFetching = false;
      state.processingItemId = undefined;
    });
  }
});

const {
  handlePaginate,
  handleSelectAll,
  handleSelect,
  handleClearFields,
  handleUpdateFieldDraftItem,
} = schedulerSlice.actions;

export {
  schedulerSlice,
  handlePaginate,
  handleSelectAll,
  handleSelect,
  getSchedulerListPayload,
  deleteSchedulerItem,
  getSchedulerItem,
  handleClearFields,
  handleUpdateFieldDraftItem,
  createSchedulerItem,
  updateSchedulerItem
}
